/**
 * Quote-draft store — the in-flight order being assembled in the quote-builder.
 *
 * - Lives in zustand for UI state, NOT in TanStack Query (which is for server state).
 * - Each fresh draft generates one idempotency key, persisted to sessionStorage so
 *   a refresh during submit retries the same key. The key is cleared when the
 *   draft resets (e.g. after a successful submit or explicit reset).
 * - Math uses decimal.js; UI renders subtotals as strings.
 */

import { Decimal } from 'decimal.js';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'tinsmith.quote-draft.idem';
/** localStorage key for the persisted draft body (see `persist` below). */
const DRAFT_STORAGE_KEY = 'tinsmith.quote-draft.v1';

export interface QuoteDraftLine {
  /** Stable client-side id; not sent to Spire. */
  clientId: string;
  whse: string;
  partNo: string;
  description: string;
  orderQty: string;
  /** Optional override. When set, line is treated as user-priced. */
  unitPrice: string | null;
  sellMeasure: string;
}

export interface QuoteDraftSignature {
  contentType: 'image/png';
  /** Raw base64 payload (no data: prefix) — sent verbatim to the API. */
  base64: string;
}

export interface QuoteDraftState {
  customerNo: string;
  type: 'W' | 'Q';
  hold: boolean;
  orderDate: string;
  requiredDate: string;
  customerPO: string;
  lines: QuoteDraftLine[];
  /** Captured customer signature. Optional — null until the rep takes one. */
  signature: QuoteDraftSignature | null;
  /**
   * Free-text note for the office / installers. Becomes a 'Tinsmith quote
   * note' CRM note on the order after submit. (Photos live in IndexedDB —
   * see QuoteDraftPhoto in outbox.ts — not in this localStorage draft.)
   */
  note: string;

  /** UUID used as the Idempotency-Key on submit. Persists across reloads. */
  idempotencyKey: string;

  // ─── actions ──────────────────────────────────────────────────────────
  setCustomerNo(no: string): void;
  setType(type: 'W' | 'Q'): void;
  setMeta(patch: Partial<Pick<QuoteDraftState, 'orderDate' | 'requiredDate' | 'customerPO' | 'hold'>>): void;
  addLine(line: Omit<QuoteDraftLine, 'clientId'>): void;
  updateLine(clientId: string, patch: Partial<QuoteDraftLine>): void;
  removeLine(clientId: string): void;
  setSignature(signature: QuoteDraftSignature | null): void;
  setNote(note: string): void;
  reset(): void;
}

function loadIdempotencyKey(): string {
  if (typeof window === 'undefined' || !window.sessionStorage) return generateUuid();
  const existing = window.sessionStorage.getItem(STORAGE_KEY);
  if (existing) return existing;
  const fresh = generateUuid();
  window.sessionStorage.setItem(STORAGE_KEY, fresh);
  return fresh;
}

function resetIdempotencyKey(): string {
  const fresh = generateUuid();
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.setItem(STORAGE_KEY, fresh);
  }
  return fresh;
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback — good enough for client-side uniqueness; never used in tests.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function today(): string {
  // LOCAL calendar date — toISOString() would yield the UTC date, which is
  // tomorrow's date every evening west of Greenwich (audit finding).
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The draft body persists to localStorage (zustand `persist`) so a page
 * refresh, iOS tab eviction, or the PWA's silent self-update can't destroy
 * an in-progress quote — including a captured signature (audit P0-11).
 * The idempotency key keeps its own sessionStorage mechanism (excluded
 * from partialize) so submit-retry semantics are unchanged.
 */
export const useQuoteDraft = create<QuoteDraftState>()(
  persist(
    (set) => ({
      customerNo: '',
      type: 'Q',
      hold: false,
      orderDate: today(),
      requiredDate: today(),
      customerPO: '',
      lines: [],
      signature: null,
      note: '',
      idempotencyKey: loadIdempotencyKey(),

      setCustomerNo: (no) => set({ customerNo: no.trim().toUpperCase() }),
      setType: (type) => set({ type }),
      setMeta: (patch) => set((state) => ({ ...state, ...patch })),
      addLine: (line) =>
        set((state) => ({
          lines: [...state.lines, { ...line, clientId: generateUuid() }],
        })),
      updateLine: (clientId, patch) =>
        set((state) => ({
          lines: state.lines.map((l) => (l.clientId === clientId ? { ...l, ...patch } : l)),
        })),
      removeLine: (clientId) =>
        set((state) => ({
          lines: state.lines.filter((l) => l.clientId !== clientId),
        })),
      setSignature: (signature) => set({ signature }),
      setNote: (note) => set({ note }),
      reset: () =>
        set({
          customerNo: '',
          type: 'Q',
          hold: false,
          orderDate: today(),
          requiredDate: today(),
          customerPO: '',
          lines: [],
          signature: null,
          note: '',
          idempotencyKey: resetIdempotencyKey(),
        }),
    }),
    {
      name: DRAFT_STORAGE_KEY,
      partialize: (s) => ({
        customerNo: s.customerNo,
        type: s.type,
        hold: s.hold,
        orderDate: s.orderDate,
        requiredDate: s.requiredDate,
        customerPO: s.customerPO,
        lines: s.lines,
        signature: s.signature,
        note: s.note,
      }),
    },
  ),
);

// ─── Selectors ────────────────────────────────────────────────────────────

export interface QuoteDraftTotals {
  /** Sum of line qty × unitPrice. Decimal-precise; string for display. */
  subtotal: string;
  /** Number of priced lines (lines with both qty>0 and unitPrice set). */
  pricedLineCount: number;
  /** Number of lines with no unitPrice — Spire will compute pricing for them. */
  unpricedLineCount: number;
}

export function computeQuoteTotals(lines: readonly QuoteDraftLine[]): QuoteDraftTotals {
  let subtotal = new Decimal(0);
  let pricedLineCount = 0;
  let unpricedLineCount = 0;
  for (const line of lines) {
    if (!line.unitPrice) {
      unpricedLineCount += 1;
      continue;
    }
    const qty = parseSafe(line.orderQty);
    const price = parseSafe(line.unitPrice);
    if (qty === null || price === null) continue;
    subtotal = subtotal.plus(qty.times(price));
    pricedLineCount += 1;
  }
  return {
    subtotal: subtotal.toString(),
    pricedLineCount,
    unpricedLineCount,
  };
}

function parseSafe(value: string): Decimal | null {
  if (!value) return null;
  try {
    return new Decimal(value);
  } catch {
    return null;
  }
}
